// Shared validation rules for both CreateNews and the edit form in ListNews,
// so "create" and "edit" enforce exactly the same real constraints instead of
// just checking a field is non-empty.
//
// Post-i18n API: each news item carries a `translations` array (one entry per
// locale) instead of flat title/header/text fields. English is required;
// Arabic is optional, but if any Arabic field is filled in, the whole Arabic
// translation must be complete.
import { REQUIRED_LOCALE_CODE } from "utils/translations";

export const MIN_TITLE_LENGTH = 4;
export const MIN_HEADER_LENGTH = 4;
export const MIN_TEXT_WORDS = 4;

// `text` is rich-text HTML (see RichTextEditor) — strip tags before counting
// words, otherwise markup like <p> or <strong> would inflate the count and
// let a near-empty article pass the minimum-length check.
export const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, " ");

const countWords = (value) => stripHtml(value).trim().split(/\s+/).filter(Boolean).length;

// An editor can produce "<p></p>" (or similar empty markup) for a blank
// editor, which is truthy as a string but has no real content.
const hasText = (value) => stripHtml(value).trim().length > 0;

const isValidUrl = (value) => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

// Validates a single locale's translation fields. `required` = true for the
// mandatory locale (English); for optional locales, only enforced if the
// author has started filling it in.
export function validateTranslation({ title, header, text, slug }, required, t) {
  const errors = {};
  const touched = (title && title.trim()) || (header && header.trim()) || hasText(text);
  if (!required && !touched) return errors;

  if (!title || !title.trim()) {
    errors.title = t("Title is required");
  } else if (title.trim().length < MIN_TITLE_LENGTH) {
    errors.title = t("Title must be at least {{count}} characters", { count: MIN_TITLE_LENGTH });
  }

  if (header && header.trim() && header.trim().length < MIN_HEADER_LENGTH) {
    errors.header = t("Header must be at least {{count}} characters", { count: MIN_HEADER_LENGTH });
  }

  if (!hasText(text)) {
    errors.text = t("Text is required");
  } else if (countWords(text) < MIN_TEXT_WORDS) {
    errors.text = t("Text must be at least {{count}} words (a real article body, not a couple of words)", { count: MIN_TEXT_WORDS });
  }

  return errors;
}

// `locales` is the dashboard's active-languages list (see useLocales() in
// utils/translations.js) — defaults to en/ar so callers that haven't been
// updated to pass it yet keep working the same as before.
export function validateNewsForm({ categoryId, translations, status, publisherBy, locales }, t) {
  const errors = {};

  if (!categoryId) {
    errors.categoryId = t("Category is required");
  }

  const localeList = locales && locales.length > 0 ? locales : [{ code: "en" }, { code: "ar" }];
  localeList.forEach(({ code }) => {
    const required = code === REQUIRED_LOCALE_CODE;
    const localeErrors = validateTranslation(translations[code] || {}, required, t);
    if (Object.keys(localeErrors).length > 0) {
      errors[code] = localeErrors;
    }
  });

  if (status === "PUBLISHED" && (!publisherBy || !publisherBy.trim())) {
    errors.publisherBy = t("Publisher name is required when status is Published");
  }

  return errors;
}

export { isValidUrl };
